import { Skeleton } from '@mui/material';
import type { GridColType, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Edit } from '@tempo-io/sui-icons';
import * as React from 'react';
import { createPseudoRandomNumberGenerator } from '@/common/utils/number';
import { useVisibleItems } from '../context/VisibleItemsContext';
import type { ItemsTableRow } from '../types';

const CIRCULAR_CONTENT_SIZE = '1.3em';
const CONTENT_HEIGHT = '1.2em';
const DEFAULT_CONTENT_WIDTH_RANGE = [40, 80] as const;
const CONTENT_WIDTH_RANGE_BY_TYPE: Partial<Record<GridColType, [number, number]>> = {
  number: [40, 60],
  string: [40, 80],
  date: [40, 60],
  dateTime: [60, 80],
  singleSelect: [40, 80],
} as const;
const randomNumberGenerator = createPseudoRandomNumberGenerator(12345);

export function EditHintWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="group/edit relative flex h-full w-full min-w-0 items-center">
      {children}
      <Edit
        aria-hidden="true"
        className="pointer-events-none absolute right-1.5 size-3.5 shrink-0 text-icon-neutral-secondary opacity-0 transition-opacity group-hover/edit:opacity-60"
      />
    </div>
  );
}

export function withEditHint<TProps extends { isEditable?: boolean }>(
  Component: React.ComponentType<TProps>,
) {
  return function WithEditHint(props: TProps) {
    if (!props.isEditable) {
      return <Component {...props} />;
    }
    return (
      <EditHintWrapper>
        <Component {...props} />
      </EditHintWrapper>
    );
  };
}

export function withSkeleton<TProps extends GridRenderCellParams<ItemsTableRow>>(
  CellComponent: React.ComponentType<TProps>,
  enableSkeleton = true,
) {
  return (props: TProps) => (
    <SkeletonCellWrapper {...props} CellComponent={CellComponent} enableSkeleton={enableSkeleton} />
  );
}

export function SkeletonCellWrapper<
  TProps extends GridRenderCellParams<ItemsTableRow> = GridRenderCellParams<ItemsTableRow>,
>({
  CellComponent,
  enableSkeleton = true,
  ...params
}: TProps & {
  CellComponent: React.ComponentType<TProps>;
  enableSkeleton?: boolean;
}) {
  const { itemsById, restrictedIds } = useVisibleItems();
  const rowId = Number(params.row.id);
  const hasItem = itemsById.has(rowId);
  const isRestricted = restrictedIds.has(rowId);

  if (enableSkeleton && !hasItem && !isRestricted) {
    return <SkeletonCell type={params.colDef.type} />;
  }

  // biome-ignore lint/suspicious/noExplicitAny: Adapter needs to accept any component props structure
  return <CellComponent {...(params as any)} />;
}

export function CellTextSkeleton() {
  return <Skeleton variant="text" width={80} height={CONTENT_HEIGHT} />;
}

function SkeletonCell({ type }: { type?: GridColType }) {
  // Memo prevents the non-circular skeleton widths changing to random widths on every render
  const skeletonProps = React.useMemo(() => {
    const isCircularContent = type === 'boolean' || type === 'actions';

    if (isCircularContent) {
      return {
        variant: 'circular',
        width: CIRCULAR_CONTENT_SIZE,
        height: CIRCULAR_CONTENT_SIZE,
      } as const;
    }

    const [min, max] = type
      ? (CONTENT_WIDTH_RANGE_BY_TYPE[type] ?? DEFAULT_CONTENT_WIDTH_RANGE)
      : DEFAULT_CONTENT_WIDTH_RANGE;

    return {
      variant: 'text',
      width: `${Math.round(randomNumberGenerator(min, max))}%`,
      height: CONTENT_HEIGHT,
    } as const;
  }, [type]);

  return <Skeleton {...skeletonProps} />;
}
